import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Search, PlusCircle, Trash2, Package, History, ArrowDown, ArrowUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  ajuste_manual:  { label: 'Ajuste Manual',   tone: 'secondary' },
  compra_cliente: { label: 'Compra Cliente',  tone: 'destructive', sign: '−' },
  compra_interna: { label: 'Compra Interna',  tone: 'destructive', sign: '−' },
  cancelacion:    { label: 'Cancelación',     tone: 'default',     sign: '+' },
};

export default function Inventory() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // ---- Tabs ----
  const [tab, setTab] = useState('products');

  // ---- Products tab state ----
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // default: active only
  const [stockFilter, setStockFilter] = useState('all'); // all|low|out|in_stock

  // ---- Movements tab state ----
  const [movements, setMovements] = useState([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movPages, setMovPages] = useState(1);
  const [movPage, setMovPage] = useState(1);
  const [movSearch, setMovSearch] = useState('');
  const [movTypeFilter, setMovTypeFilter] = useState('');
  const [movLoading, setMovLoading] = useState(true);

  // ---- Adjust dialog ----
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ product_id: '', product_name: '', current_stock: 0, new_stock: 0, reason: '' });

  // -------- Fetch products list with current stock --------
  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const params = { page: 1, limit: 100, sort: 'newest', status: statusFilter || 'all' };
      if (productSearch) params.search = productSearch;
      const res = await axios.get(`${API}/products`, { params });
      setProducts(res.data.products || []);
    } catch (err) {
      console.error(err);
      toast.error('No se pudo cargar el inventario');
    } finally {
      setProductsLoading(false);
    }
  }, [productSearch, statusFilter]);

  // -------- Fetch movements history --------
  const fetchMovements = useCallback(async () => {
    setMovLoading(true);
    try {
      const params = { page: movPage, limit: 30 };
      if (movSearch) params.search = movSearch;
      if (movTypeFilter) params.movement_type = movTypeFilter;
      const res = await axios.get(`${API}/admin/inventory`, { params, headers });
      setMovements(res.data.movements);
      setMovTotal(res.data.total);
      setMovPages(res.data.pages);
    } catch (err) { console.error(err); } finally { setMovLoading(false); }
  }, [movPage, movSearch, movTypeFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { if (tab === 'movements') fetchMovements(); }, [tab, fetchMovements]);

  // ---- Computed: filter products on client side for stock filter ----
  const filteredProducts = products.filter(p => {
    if (stockFilter === 'low')     return p.stock > 0 && p.stock <= 5;
    if (stockFilter === 'out')     return p.stock === 0;
    if (stockFilter === 'in_stock') return p.stock > 0;
    return true;
  });

  const totalStock = filteredProducts.reduce((s, p) => s + (p.stock || 0), 0);

  // ---- Stats for header chips ----
  const stats = {
    total: products.length,
    active: products.filter(p => p.status === 'active').length,
    out_of_stock: products.filter(p => p.status === 'active' && p.stock === 0).length,
    low_stock: products.filter(p => p.status === 'active' && p.stock > 0 && p.stock <= 5).length,
  };

  // ---- Open adjust dialog for a specific product ----
  const openAdjust = (p) => {
    setAdjustForm({
      product_id: p.id,
      product_name: p.name,
      current_stock: p.stock,
      new_stock: p.stock,
      reason: '',
    });
    setAdjustOpen(true);
  };

  const handleAdjust = async () => {
    if (!adjustForm.product_id || !adjustForm.reason.trim()) {
      toast.error('Indica un motivo para el ajuste');
      return;
    }
    if (adjustForm.new_stock < 0) {
      toast.error('El stock no puede ser negativo');
      return;
    }
    try {
      await axios.post(`${API}/admin/inventory/adjust`, {
        product_id: adjustForm.product_id,
        new_stock: adjustForm.new_stock,
        reason: adjustForm.reason,
      }, { headers });
      const delta = adjustForm.new_stock - adjustForm.current_stock;
      toast.success(
        delta === 0
          ? 'Stock confirmado (sin cambio)'
          : delta > 0
            ? `Stock incrementado en +${delta} unidades`
            : `Stock reducido en ${delta} unidades`
      );
      setAdjustOpen(false);
      fetchProducts();
      if (tab === 'movements') fetchMovements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo ajustar el stock');
    }
  };

  const handleDeleteMovement = async (movementId) => {
    if (!window.confirm('¿Eliminar este movimiento del historial? Esto solo borra el registro, no revierte el stock.')) return;
    try {
      await axios.delete(`${API}/admin/inventory/${movementId}`, { headers });
      toast.success('Movimiento eliminado del historial');
      fetchMovements();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-semibold">Inventario</h1>
      </div>

      {/* KPI strip — focused on ACTIVE products */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Total productos</p>
          <p className="text-2xl font-display font-semibold tabular-nums">{stats.total}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Activos</p>
          <p className="text-2xl font-display font-semibold tabular-nums">{stats.active}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Stock bajo (activos)</p>
          <p className="text-2xl font-display font-semibold tabular-nums text-amber-500">{stats.low_stock}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Agotados (activos)</p>
          <p className="text-2xl font-display font-semibold tabular-nums text-destructive">{stats.out_of_stock}</p>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="products" className="gap-2" data-testid="inv-tab-products">
            <Package className="h-4 w-4" /> Productos
          </TabsTrigger>
          <TabsTrigger value="movements" className="gap-2" data-testid="inv-tab-movements">
            <History className="h-4 w-4" /> Historial de movimientos
          </TabsTrigger>
        </TabsList>

        {/* ============================================================ */}
        {/* PRODUCTS TAB — list of products with current stock           */}
        {/* ============================================================ */}
        <TabsContent value="products" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar producto o SKU..."
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                className="pl-9"
                data-testid="inv-product-search"
              />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? 'all' : v)}>
              <SelectTrigger className="w-[150px]" data-testid="inv-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Solo activos</SelectItem>
                <SelectItem value="inactive">Solo inactivos</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger className="w-[170px]" data-testid="inv-stock-filter"><SelectValue placeholder="Stock" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los stocks</SelectItem>
                <SelectItem value="in_stock">Con stock</SelectItem>
                <SelectItem value="low">Stock bajo (1-5)</SelectItem>
                <SelectItem value="out">Agotados</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground self-center ml-auto">
              {filteredProducts.length} productos · Total: <span className="tabular-nums font-medium text-foreground">{totalStock}</span> unidades
            </span>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-secondary/30">
                      <th className="px-3 py-2 text-left font-medium">Producto</th>
                      <th className="px-3 py-2 text-left font-medium">SKU</th>
                      <th className="px-3 py-2 text-left font-medium">Marca</th>
                      <th className="px-3 py-2 text-center font-medium">Estado</th>
                      <th className="px-3 py-2 text-center font-medium">Stock actual</th>
                      <th className="px-3 py-2 text-center font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productsLoading ? (
                      <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Cargando...</td></tr>
                    ) : filteredProducts.length === 0 ? (
                      <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin productos que coincidan</td></tr>
                    ) : filteredProducts
                      .sort((a, b) => (a.stock || 0) - (b.stock || 0))
                      .map(p => (
                      <tr key={p.id} className="border-b hover:bg-secondary/20 transition-colors duration-150" data-testid={`inv-product-row-${p.id}`}>
                        <td className="px-3 py-2 font-medium max-w-[320px]"><span className="line-clamp-2">{p.name}</span></td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{p.sku || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{p.brand || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant={p.status === 'active' ? 'secondary' : 'outline'} className={p.status === 'inactive' ? 'text-muted-foreground' : ''}>
                            {p.status === 'active' ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1.5" data-testid={`inv-stock-${p.id}`}>
                            <span className={`tabular-nums font-semibold text-base ${
                              p.stock === 0 ? 'text-destructive' :
                              p.stock <= 5 && p.status === 'active' ? 'text-amber-500' :
                              'text-foreground'
                            }`}>{p.stock ?? 0}</span>
                            {p.stock === 0 && p.status === 'active' && (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Agotado</Badge>
                            )}
                            {p.stock > 0 && p.stock <= 5 && p.status === 'active' && (
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => openAdjust(p)}
                            data-testid={`inv-adjust-btn-${p.id}`}
                          >
                            <PlusCircle className="h-3.5 w-3.5" /> Ajustar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============================================================ */}
        {/* MOVEMENTS TAB — chronological history of stock changes      */}
        {/* ============================================================ */}
        <TabsContent value="movements" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar producto..." value={movSearch} onChange={e => { setMovSearch(e.target.value); setMovPage(1); }} className="pl-9" />
            </div>
            <Select value={movTypeFilter || 'all'} onValueChange={v => { setMovTypeFilter(v === 'all' ? '' : v); setMovPage(1); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground self-center">{movTotal} movimientos</span>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-secondary/30">
                      <th className="px-3 py-2 text-left font-medium">Producto</th>
                      <th className="px-3 py-2 text-left font-medium">SKU</th>
                      <th className="px-3 py-2 text-center font-medium">Antes</th>
                      <th className="px-3 py-2 text-center font-medium">Cambio</th>
                      <th className="px-3 py-2 text-center font-medium">Después</th>
                      <th className="px-3 py-2 text-left font-medium">Tipo</th>
                      <th className="px-3 py-2 text-left font-medium">Razón</th>
                      <th className="px-3 py-2 text-left font-medium">Usuario</th>
                      <th className="px-3 py-2 text-left font-medium">Fecha</th>
                      <th className="px-3 py-2 text-center font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {movLoading ? (
                      <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Cargando...</td></tr>
                    ) : movements.length === 0 ? (
                      <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin movimientos registrados</td></tr>
                    ) : movements.map(m => {
                      const meta = TYPE_META[m.movement_type] || { label: m.movement_type, tone: 'outline' };
                      const delta = (m.quantity_after ?? 0) - (m.quantity_before ?? 0);
                      const deltaColor = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-destructive' : 'text-muted-foreground';
                      const DeltaIcon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : null;
                      return (
                        <tr key={m.id} className="border-b hover:bg-secondary/20 transition-colors duration-150">
                          <td className="px-3 py-2 font-medium max-w-[260px]"><span className="line-clamp-2">{m.product_name}</span></td>
                          <td className="px-3 py-2 font-mono text-xs">{m.product_sku || '—'}</td>
                          <td className="px-3 py-2 text-center tabular-nums">{m.quantity_before}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center gap-0.5 tabular-nums font-medium ${deltaColor}`}>
                              {DeltaIcon && <DeltaIcon className="h-3 w-3" />}
                              {delta > 0 ? '+' : ''}{delta}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums font-semibold">{m.quantity_after}</td>
                          <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{meta.label}</Badge></td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">{m.reason}</td>
                          <td className="px-3 py-2">{m.user_name || '—'}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString('es-MX')}</td>
                          <td className="px-3 py-2 text-center">
                            <Button variant="ghost" size="sm" className="text-destructive h-7 w-7 p-0" onClick={() => handleDeleteMovement(m.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {movPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" disabled={movPage <= 1} onClick={() => setMovPage(movPage - 1)}>Anterior</Button>
              <span className="flex items-center text-sm text-muted-foreground">Página {movPage} de {movPages}</span>
              <Button variant="outline" size="sm" disabled={movPage >= movPages} onClick={() => setMovPage(movPage + 1)}>Siguiente</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Adjust dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar stock</DialogTitle>
            <DialogDescription>
              Modifica manualmente el inventario del producto. Se registrará un movimiento de auditoría.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Producto</Label>
              <p className="font-medium text-sm">{adjustForm.product_name}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Stock actual</Label>
                <Input value={adjustForm.current_stock} disabled className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Nuevo stock</Label>
                <Input
                  type="number"
                  min="0"
                  value={adjustForm.new_stock}
                  onChange={e => setAdjustForm({...adjustForm, new_stock: Math.max(0, parseInt(e.target.value) || 0)})}
                  className="font-mono"
                  data-testid="adjust-new-stock"
                />
              </div>
            </div>
            {adjustForm.new_stock !== adjustForm.current_stock && (
              <p className="text-xs text-muted-foreground">
                Cambio: <span className={adjustForm.new_stock > adjustForm.current_stock ? 'text-emerald-600 font-medium' : 'text-destructive font-medium'}>
                  {adjustForm.new_stock > adjustForm.current_stock ? '+' : ''}{adjustForm.new_stock - adjustForm.current_stock} unidades
                </span>
              </p>
            )}
            <div className="space-y-1">
              <Label>Motivo del ajuste</Label>
              <Input
                value={adjustForm.reason}
                onChange={e => setAdjustForm({...adjustForm, reason: e.target.value})}
                placeholder="Ej: Recepción de proveedor, merma, conteo físico..."
                data-testid="adjust-reason"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancelar</Button>
              <Button onClick={handleAdjust} data-testid="adjust-confirm">Confirmar ajuste</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
